// src/components/CoachPosts.js
// "from Danielle" — coach posts section for the Dashboard
// Shows latest 3 active posts (< 30 days old) in glass cards
// Only rendered for subscribers, admin, or friend

import React, { useState, useEffect } from 'react';
import { getRecentCoachPosts } from '../services/coachPosts';
import CoachPostImage from './CoachPostImage';

// ── Relative time helper ──
function timeAgo(isoString) {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function CoachPosts({ coachAvatarUrl }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getRecentCoachPosts(3).then((p) => {
            setPosts(p);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    // Don't render anything if loading or no posts
    if (loading || posts.length === 0) return null;

    return (
        <section style={{ marginBottom: 28 }}>
            {/* Section header */}
            <div style={{ marginBottom: 18 }}>
                <h2 style={{
                    fontFamily: 'var(--mom-font-serif)', fontSize: 20,
                    fontStyle: 'italic', fontWeight: 600,
                    color: 'var(--mom-charcoal)', marginBottom: 8,
                }}>
                    from Danielle
                </h2>
                <div style={{
                    width: 32, height: 2, borderRadius: 1,
                    background: 'linear-gradient(90deg, var(--mom-primary), var(--mom-primary-light))',
                }} />
            </div>

            {/* Post cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {posts.map((post, i) => (
                    <div
                        key={post.id}
                        className="slide-up"
                        style={{
                            position: 'relative',
                            background: 'rgba(255,255,255,0.55)',
                            backdropFilter: 'blur(20px) saturate(1.4)',
                            WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                            borderRadius: 20,
                            padding: 24,
                            boxShadow: 'var(--shadow-sm)',
                            border: '1px solid rgba(255,255,255,0.55)',
                            overflow: 'hidden',
                            animationDelay: `${i * 0.1}s`,
                        }}
                    >
                        {/* Specular highlight */}
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, height: '50%',
                            background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.28) 0%, transparent 60%)',
                            pointerEvents: 'none', zIndex: 0, borderRadius: '20px 20px 0 0',
                        }} />

                        <div style={{ position: 'relative', zIndex: 1 }}>
                            {/* Author + timestamp */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                {/* Coach avatar — admin-uploaded image when set, brand-gradient
                                    ★ fallback otherwise. The image circle preserves the same
                                    32px round footprint so layout never shifts. */}
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: coachAvatarUrl
                                        ? 'transparent'
                                        : 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                }}>
                                    {coachAvatarUrl ? (
                                        <img src={coachAvatarUrl} alt="" referrerPolicy="no-referrer"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                    ) : (
                                        <span style={{
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700,
                                            color: '#fff',
                                        }}>★</span>
                                    )}
                                </div>
                                <span style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                                    color: 'var(--mom-charcoal)',
                                }}>Danielle</span>
                                <span style={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    background: 'var(--mom-primary)', display: 'inline-block',
                                }} />
                                <span style={{
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                                    color: 'var(--mom-warm-gray)',
                                }}>{timeAgo(post.createdAt)}</span>
                            </div>

                            {/* Post body */}
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 15,
                                color: 'var(--mom-charcoal)', lineHeight: 1.7,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                margin: 0,
                            }}>{post.body}</p>

                            {/* Optional image — shown whole at the size Danielle chose
                                (CoachPostImage is shared with the admin preview). */}
                            {post.imageUrl && (
                                <div style={{ marginTop: 16 }}>
                                    <CoachPostImage src={post.imageUrl} size={post.imageSize} />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

export default CoachPosts;
